/**
 * Module 8: feedback, rules, languages, phone (2 minutes). Feedback does not change the
 * case, Rules and countries, mail in another language, the phone and wide layouts.
 * Round 3 (12 Oct 2026): Simplified Technical English.
 *
 * 13 Oct 2026: slide x8 and its check x9, the proposals of the deeper search ("Why",
 * "Confirm", "Not this one", "Provisional", "Look again"). The feature is switched on for
 * each country, so the slide says that a case page can look as before.
 */

import type { Module } from "@/features/training/types";

import { checkSlide } from "../check-slide";

export const MODULE: Module = {
  id: "extras",
  title: "Feedback, rules, languages, phone",
  menuTitle: "Extras",
  minutes: 2,
  kind: "lesson",
  goals: [
    "Know that feedback is different from a correction of the case.",
    "Find the rules of your country.",
    "Read a mail in another language.",
    "Use I2Q on your phone.",
    "Read a proposal of the deeper search before you confirm it.",
  ],
  helpTopics: ["feedback", "rules-and-countries", "translation", "deeper-search"],
  slides: [
    {
      id: "x1",
      tone: "light",
      title: "Feedback tells the build team. It does not change the case.",
      job: "Correct the case first, then give feedback.",
      body: [
        {
          kind: "text",
          text: "To correct the case, use the row buttons: `Confirm`, `Pick item`, or `No item` in the row's `Details`. **Feedback tells the build team. It does not change the case.**",
        },
        {
          kind: "text",
          text: "`Give feedback` is in the header of every page and under `More` on a case. `Was this right?` sits in a row's `Details` and in the `Details` sheet. Your feedback appears under `My feedback`.",
        },
      ],
      interaction: {
        id: "x1-sort",
        kind: "sort",
        required: true,
        prompt: "Which buttons change the case?",
        bins: ["Changes the case", "Tells the build team"],
        cards: [
          {
            id: "x1-no-item",
            text: "No item",
            bin: 0,
            why: "It takes the item off the row. The reply tells the customer.",
          },
          {
            id: "x1-pick-item",
            text: "Pick item",
            bin: 0,
            why: "It puts another item on the row.",
          },
          {
            id: "x1-find-customer",
            text: "Find customer",
            bin: 0,
            why: "It sets another customer on the case.",
          },
          {
            id: "x1-wrong",
            text: "Was this right? Wrong",
            bin: 1,
            why: "It tells the build team that the item was wrong. The line stays as it is.",
          },
          {
            id: "x1-feedback",
            text: "Give feedback",
            bin: 1,
            why: "It sends your note to the build team. The case stays as it is.",
          },
        ],
      },
      check: "Correct the case with Pick item, No item or Find customer. Then tell the build team.",
      labels: ["Give feedback", "Was this right?", "My feedback", "More", "Details"],
    },
    {
      id: "x2",
      tone: "grey",
      title: "When you need to know a country rule",
      job: "When you need to know a country rule, open Rules and countries.",
      path: ["Sidebar", "Rules and countries"],
      body: [
        {
          kind: "text",
          text: "The guide opens on your own area. Tabs: `Countries`, `All rules` and `How I2Q works`. Each rule has a Why link to the glossary.",
        },
        {
          kind: "note",
          tone: "info",
          title: "Area checks are not faults of your case",
          text: "Some checks hit every case in an area, for example no mapping of the item group yet. These checks are known. Your case is not wrong because of them.",
        },
      ],
      check:
        "A check on every case of your area is a known gap. Look it up in Rules and countries.",
      labels: ["Rules and countries", "Countries", "All rules", "How I2Q works"],
    },
    {
      id: "x3",
      tone: "light",
      title: "Mail in another language",
      job: "When you need to read a mail in another language, compare the translation with the original.",
      path: ["Case", "Mail", "Show original"],
      body: [
        {
          kind: "text",
          text: "I2Q translates the mail for you. The mail card says `Translated from` and the language. `Show original` shows the customer's own words. `Show translation` switches back.",
        },
      ],
      interaction: {
        id: "x3-mail",
        kind: "hotspots",
        required: false,
        minOpen: 1,
        stage: { view: "case", caseId: "PRACTICE-0002", part: "mail" },
        spots: [
          {
            anchor: "case-mail",
            label: "The mail",
            text: "A Danish mail, translated. Check the item numbers and the quantities in the original.",
          },
        ],
      },
      check: "Numbers and item numbers: always check them in the original.",
      labels: ["Show original", "Show translation", "Translated from"],
    },
    {
      id: "x4",
      tone: "grey",
      title: "On your phone",
      job: "Use I2Q on your phone when you are away from your desk.",
      body: [
        {
          kind: "list",
          items: [
            "Queue rows show as cards: customer, subject, the action word, who has it.",
            "`Filters` is one button on every width. The number on it counts your filters.",
            "On every width the case page stacks the mail card, the draft and the one action in the bar. A long mail is folded after 6 lines. `Read all` shows the rest.",
            "On a phone the draft rows are cards with the same icons. From 768 px wide they form the table.",
          ],
        },
        {
          kind: "keys",
          keys: [
            { key: "?", does: "Show all keyboard shortcuts" },
            { key: "j", does: "Next case or row" },
            { key: "k", does: "Previous case or row" },
            { key: "r", does: "Copy the reply" },
          ],
        },
        {
          kind: "note",
          tone: "info",
          title: "Outlook add-in",
          text: "The I2Q pane for Outlook is built, but not live yet. IT tells you when you can use it.",
        },
      ],
      check: "On a phone, check the rows as carefully as on your desk.",
      labels: ["Filters", "Read all"],
    },
    {
      id: "x8",
      tone: "light",
      title: "When the deeper search proposes a customer or an item",
      job: "Read the proposal and its lookups. Then confirm it or reject it.",
      path: ["Case", "Customer line"],
      body: [
        {
          kind: "text",
          text: "On some cases the customer line or a row shows the tag `Proposed by the deeper search`. The deeper search looked things up in the customer data and in our earlier quotations. It proposes. You decide.",
        },
        {
          kind: "list",
          items: [
            "`Why` lists each lookup with the rows it found. Read it before you confirm.",
            "`Confirm` confirms the proposal. `Not this one` rejects it, and the history of the case keeps that.",
            "A proposal with a doubt is never the first account on the line. Read the doubt first.",
            "`Provisional` marks a list price for the suggested account. It is no price for a quotation. Confirm the customer to get the price.",
            "`Look again` asks the deeper search for the case, or for one line, again. `Looking again` shows while the request waits.",
          ],
        },
        {
          kind: "note",
          tone: "info",
          title: "Only in some countries",
          text: "The proposals and `Look again` are switched on for each country. Without the switch the case page looks as before.",
        },
      ],
      check:
        "Read Why before you confirm a proposal. A provisional price is no price for a quotation.",
      labels: [
        "Proposed by the deeper search",
        "Why",
        "Confirm",
        "Not this one",
        "Provisional",
        "Look again",
        "Looking again",
      ],
    },
    checkSlide({
      id: "x5",
      tone: "light",
      title: "Check: feedback",
      topic: "feedback",
      stem: "Which button changes the case?",
      answers: [
        ["Give feedback", false, "No. Feedback tells the build team."],
        ["Was this right? Wrong", false, "No. That is feedback. The row stays as it is."],
        ["Pick item", true, "Right. Pick item puts the item you picked on the row."],
        ["My feedback", false, "No. That page lists the feedback you gave."],
      ],
      check: "Correct the case first. Then give feedback.",
      labels: ["Pick item", "Give feedback", "My feedback"],
    }),
    checkSlide({
      id: "x6",
      tone: "grey",
      title: "Check: area checks",
      topic: "lines",
      stem: "Every case in your area shows the same check about item groups. What does it mean?",
      answers: [
        ["Your case is wrong.", false, "No. The check hits every case in the area."],
        [
          "A known gap for the whole area. Look it up in Rules and countries.",
          true,
          "Right. It is not a fault of your case.",
        ],
        ["You must stop quoting.", false, "No. Quote as usual and check the lines."],
        ["I2Q is down.", false, "No. I2Q works. The check is a known gap."],
      ],
      check: "Same check on every case? Look in Rules and countries.",
      labels: ["Rules and countries"],
    }),
    checkSlide({
      id: "x7",
      tone: "light",
      title: "Check: another language",
      topic: "lines",
      stem: "The mail is in Danish. Where do you check the item numbers?",
      answers: [
        ["In the translation only.", false, "No. A translation can change a number or a unit."],
        [
          "In the original. Click `Show original`.",
          true,
          "Right. Numbers and item numbers come from the customer's own words.",
        ],
        ["Ask the customer to write in English.", false, "No. I2Q translates for you."],
        ["Nowhere. I2Q checked them.", false, "No. You check the lines."],
      ],
      check: "Numbers: always in the original.",
      labels: ["Show original"],
    }),
    checkSlide({
      id: "x9",
      tone: "grey",
      title: "Check: a provisional price",
      topic: "prices",
      stem: "A row shows a price with the chip `Provisional`. What do you do before you use it?",
      answers: [
        [
          "Use it in the quotation. I2Q found it.",
          false,
          "No. A provisional price is for a suggested customer. It is no price for a quotation.",
        ],
        [
          "Confirm the customer. I2Q then reads the price for that account.",
          true,
          "Right. The price counts after the customer is confirmed.",
        ],
        [
          "Click `Not this one`.",
          false,
          "No. That rejects a proposal. It does not give you a price.",
        ],
        [
          "Set the same price with `Set price`.",
          false,
          "No. Confirm the customer first. Then check the price that I2Q reads.",
        ],
      ],
      check: "Provisional: confirm the customer first.",
      labels: ["Provisional", "Not this one", "Set price"],
    }),
  ],
};
